import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
  EMPTY_PENDING,
  addReplacement,
  buildBatchedEditCartItems,
  hasPendingChanges as hasPending,
  queueRemoveItem,
  restoreItems,
} from "utils/cart";
import {
  recommendationsByItemSelector,
  recommendationsByItemLoadingSelector,
  cartLoadingSelector,
  cartErrorSelector,
} from "../../selectors";
import Hero from "./Hero";
import LenderList from "./LenderList";
import OrderSummary from "./OrderSummary";
import CTAFooter from "./CTAFooter";
import {
  buildScreenViewModel,
  computeRemainingDeficit,
  getOffersForMode,
  pickHighestApproval,
  readChangeFlow,
  resolveCta,
} from "./helpers";
import { buildSelectedOfferPayload } from "../helpers";
import "./styles.scss";

class CartApprovalScreen extends PureComponent {
  state = {
    selectedLenderProvider: null,
    changingItemId: null,
    changingDeficit: null,
    pendingChanges: EMPTY_PENDING,
  };

  componentDidMount() {
    this.ensureDefaultLender();
  }

  componentDidUpdate() {
    this.ensureDefaultLender();
  }

  ensureDefaultLender = () => {
    const offers = this.getAllOffers();
    const { selectedLenderProvider } = this.state;
    const stillValid =
      selectedLenderProvider &&
      offers.some((o) => o.specific_provider_name === selectedLenderProvider);
    if (stillValid) return;
    const best = pickHighestApproval(offers);
    if (best && best.specific_provider_name !== selectedLenderProvider) {
      this.setState({ selectedLenderProvider: best.specific_provider_name });
    }
  };

  selectLender = (provider) => {
    this.setState({ selectedLenderProvider: provider });
    this.logEvent("lender_selected", { provider });
  };

  startChange = (itemId) => {
    const { selectedLenderProvider } = this.state;
    if (!selectedLenderProvider || !itemId) return;
    const approvedAmount = this.getSelectedApproved();
    if (!approvedAmount) return;
    const remainingDeficit = computeRemainingDeficit(
      approvedAmount,
      this.getCurrentCartTotal()
    );
    this.setState({ changingItemId: itemId, changingDeficit: remainingDeficit });
    this.logEvent("change_open", { itemId, remainingDeficit });
    const { getRecommendationsForItem } = this.props;
    if (getRecommendationsForItem) {
      getRecommendationsForItem({ itemId, approvedAmount, remainingDeficit });
    }
  };

  cancelChange = () =>
    this.setState({ changingItemId: null, changingDeficit: null });

  applyReplacement = (recItem, recQuantity) => {
    const { changingItemId } = this.state;
    if (!changingItemId) return;
    const qty = recQuantity || this.getOriginalQuantity(changingItemId);
    this.setState((prev) => ({
      pendingChanges: addReplacement(
        prev.pendingChanges,
        changingItemId,
        recItem,
        qty
      ),
      changingItemId: null,
      changingDeficit: null,
    }));
    this.logEvent("replacement_chosen");
  };

  queueRemove = (itemId) => {
    this.setState((prev) => ({
      pendingChanges: queueRemoveItem(prev.pendingChanges, itemId),
    }));
    this.logEvent("remove_queued", { itemId });
  };

  addBack = (itemIds) => {
    this.setState((prev) => ({
      pendingChanges: restoreItems(prev.pendingChanges, itemIds),
    }));
  };

  submitCartChanges = () => {
    const { editCart, items } = this.props;
    if (!editCart || !hasPending(this.state.pendingChanges)) return;
    const lineItems = (items && items.line_items) || [];
    const payloadItems = buildBatchedEditCartItems(
      lineItems,
      this.state.pendingChanges
    );
    editCart({ items: payloadItems });
    this.logEvent("update_cart");
  };

  continueWithLender = () => {
    const { selectOffer } = this.props;
    const offer = this.findSelectedOffer();
    if (!offer || !selectOffer) return;
    this.logEvent("continue_with_lender", {
      provider: offer.specific_provider_name,
    });

    const { selectedOffer: payload, body } = buildSelectedOfferPayload(offer);
    selectOffer({ selectedOffer: payload, body });
  };

  logEvent = (suffix, extra = {}) => {
    const { logEvent, mode } = this.props;
    if (!logEvent) return;
    logEvent({ name: `${mode}_${suffix}`, ...extra });
  };

  getOriginalQuantity = (itemId) => {
    const lineItems = (this.props.items && this.props.items.line_items) || [];
    const item = lineItems.find((i) => i.id === itemId);
    return (item && item.quantity) || 1;
  };

  getAllOffers = () => {
    const { mode, availableApplications, notAvailableApplications } = this.props;
    return getOffersForMode(mode, availableApplications, notAvailableApplications);
  };

  findSelectedOffer = () => {
    const { selectedLenderProvider } = this.state;
    if (!selectedLenderProvider) return null;
    return (
      this.getAllOffers().find(
        (o) => o.specific_provider_name === selectedLenderProvider
      ) || null
    );
  };

  getSelectedApproved = () => {
    const offer = this.findSelectedOffer();
    return offer ? parseFloat(offer.approved_amount) || 0 : null;
  };

  getCurrentCartTotal = () => {
    const { items, cartAmount } = this.props;
    const vm = buildScreenViewModel({
      mode: this.props.mode,
      items,
      cartAmount,
      pendingChanges: this.state.pendingChanges,
      allOffers: this.getAllOffers(),
      selectedLenderProvider: this.state.selectedLenderProvider,
    });
    return vm.newCartTotal;
  };

  render() {
    const {
      mode,
      items,
      recommendationsByItem,
      recommendationsByItemLoading,
      cartError,
      cartModalLoading,
    } = this.props;
    const {
      selectedLenderProvider,
      changingItemId,
      changingDeficit,
      pendingChanges,
    } = this.state;

    const allOffers = this.getAllOffers();
    const vm = buildScreenViewModel({
      mode,
      items,
      cartAmount: this.props.cartAmount,
      pendingChanges,
      allOffers,
      selectedLenderProvider,
    });

    const hasPendingChanges = hasPending(pendingChanges);
    const cta = resolveCta({
      mode,
      submitting: cartModalLoading,
      hasPending: hasPendingChanges,
      fits: vm.fits,
      selectedOffer: vm.selectedOffer,
      selectedLenderName: vm.selectedLenderName,
      overForSelected: vm.overForSelected,
      onSubmitChanges: this.submitCartChanges,
      onContinueWithLender: this.continueWithLender,
    });

    const changeFlow = readChangeFlow({
      recommendationsByItem,
      recommendationsByItemLoading,
      itemId: changingItemId,
      approvedAmount: vm.selectedApproved,
      remainingDeficit: changingDeficit,
    });

    const congratsOffer =
      vm.selectedOffer || (allOffers.length === 1 ? allOffers[0] : null);

    return (
      <div className={"cart-approval-screen cart-approval-screen--" + (mode || "over")}>
        <Hero
          congratsOffer={congratsOffer}
          numLenders={allOffers.length}
          selectedApproved={
            congratsOffer ? parseFloat(congratsOffer.approved_amount) || 0 : null
          }
          cartTotal={vm.newCartTotal}
        />

        <div className="cart-approval-screen__lenders">
          <LenderList
            offers={allOffers}
            cartTotal={vm.newCartTotal}
            selectedLenderProvider={selectedLenderProvider}
            onSelectLender={this.selectLender}
          />
        </div>

        <div className="cart-approval-screen__summary">
          <OrderSummary
            items={{ ...items, line_items: vm.displayLineItems }}
            changeFlow={changeFlow}
            changingTireItemId={changingItemId}
            onStartChangeTire={this.startChange}
            onCancelChangeTire={this.cancelChange}
            onApplyReplacement={this.applyReplacement}
            onRemove={this.queueRemove}
            onAddBack={this.addBack}
            changeDisabled={!selectedLenderProvider}
            changeDisabledHint="Select a lender first"
          />
        </div>

        <CTAFooter
          helperText={cta.helper}
          hintText={cta.hint}
          ctaText={cta.text}
          disabled={cta.disabled}
          onClick={cta.onClick}
          errorText={
            cartError && !cartModalLoading
              ? "We couldn't update your cart. Please try again."
              : null
          }
        />
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  recommendationsByItem: recommendationsByItemSelector(),
  recommendationsByItemLoading: recommendationsByItemLoadingSelector(),
  cartModalLoading: cartLoadingSelector(),
  cartError: cartErrorSelector(),
});

export default connect(mapStateToProps)(CartApprovalScreen);
